/**
 * Phase 68 Plan 03 — Power Health API client.
 *
 * Mirrors the Plan 68-02 locked REST contract (see 68-02-SUMMARY.md
 * "Hand-off to Plan 68-03"). All endpoints are admin-only and live
 * under `/api/v1/admin/power-health/*`. RBAC: admin / super_admin /
 * circuit_admin pass at endpoint level; circuit_admin is implicitly
 * scoped to its own circuit by the backend filter.
 *
 *   GET /anomaly-heatmap?days=&kiosk_ids=&severity_min=&flagged=
 *   GET /snapshots/{cpu_id}?date=&cursor=&limit=
 *   GET /events/{cpu_id}?date=&type=&severity=&cursor=&limit=
 *   GET /{cpu_id}/timeline?hours=24
 *   GET /sun-times?cpu_id=&date=YYYY-MM-DD   → 404 → null per D-LL-04
 *   GET /summary
 *   GET /flagged-count
 *
 * 401 / 429 / non-2xx handling lives in the shared `apiRequest` wrapper
 * (authBus + rateLimitStore + ApiError). The single exception is
 * `fetchSunTimes`, which translates 404 into `null` instead of throwing —
 * a NULL lat/lon machine is the documented empty-state (D-LL-04), not
 * an error condition.
 *
 * @see .planning/phases/68-power-telemetry-offgrid-fleet/68-02-SUMMARY.md
 * @see .planning/phases/68-power-telemetry-offgrid-fleet/68-UI-SPEC.md §6
 */

import { apiGet, type ApiError } from "./client";

// ---------------------------------------------------------------
// EVT taxonomy (matches backend power_health_spec.py EVT_WEIGHTS keys)
// ---------------------------------------------------------------

export type EvtType =
  | "kernel_panic"
  | "qthread_segfault"
  | "power_key_spurious"
  | "graceful_poweroff_logind"
  | "over_current"
  | "usb_disconnect"
  | "usb_reset";

export type EvtSeverity = "info" | "warning" | "error" | "critical";

// ---------------------------------------------------------------
// Response shapes (frozen with Plan 68-02 backend)
// ---------------------------------------------------------------

export interface HeatmapCell {
  cpu_id: string;
  cpu_label: string;
  date: string; // ISO YYYY-MM-DD
  score: number;
  event_count: number;
  flagged: boolean;
}

export interface HeatmapResponse {
  cells: HeatmapCell[];
  total_kiosks: number;
}

export interface PowerHealthSnapshotRow {
  id: number;
  cpu_id: string;
  ts: string; // ISO timestamp
  cpu_khz: number[];
  load_1m: number;
  uptime_s: number;
  boot_id: string;
  usb_devices: Array<[string, string, string]>; // [bus, vid_pid, status]
  kernel_evt_24h: Record<string, number>;
  boot_count_24h: number;
  thermal_zone0_c: number | null;
}

export interface SnapshotPage {
  rows: PowerHealthSnapshotRow[];
  next_cursor: string | null;
  total_estimated: number;
}

export interface PowerHealthEventRow {
  id: number;
  cpu_id: string;
  ts: string; // ISO timestamp
  type: string;
  severity: EvtSeverity;
  details: Record<string, unknown>;
}

export interface EventPage {
  rows: PowerHealthEventRow[];
  next_cursor: string | null;
  total_estimated: number;
}

export interface TimelineBucket {
  hour: string; // ISO timestamp truncated to hour
  snapshot_count: number;
  avg_load_1m: number;
  avg_thermal_c: number | null;
}

export interface TimelineResponse {
  buckets: TimelineBucket[];
  events: PowerHealthEventRow[];
}

export interface SunTimesResponse {
  sunrise: string; // ISO timestamp
  noon: string;
  sunset: string;
  timezone: string; // IANA tz name (e.g., "Europe/Rome")
}

export interface SummaryResponse {
  total_kiosks: number;
  flagged_now: number;
  events_24h: number;
  worst_kiosk_cpu_id: string | null;
}

export interface FlaggedCountResponse {
  count: number;
  since_ts: number | null;
  rising_edge: boolean;
}

// ---------------------------------------------------------------
// Filter shapes (form-level, consumed by FilterBar + qk factories)
// ---------------------------------------------------------------

export interface HeatmapFilters {
  kioskIds?: string[];
  severityMin?: 0 | 1 | 6 | 16;
  flaggedOnly?: boolean;
}

export interface EventFilters {
  date?: string; // YYYY-MM-DD
  type?: EvtType;
  severity?: EvtSeverity;
}

// ---------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------

const BASE = "/admin/power-health";

function buildHeatmapQuery(days: number, filters?: HeatmapFilters): string {
  const params = new URLSearchParams();
  params.set("days", String(days));
  if (filters?.kioskIds && filters.kioskIds.length > 0) {
    params.set("kiosk_ids", filters.kioskIds.join(","));
  }
  if (filters?.severityMin != null) {
    params.set("severity_min", String(filters.severityMin));
  }
  if (filters?.flaggedOnly) {
    params.set("flagged", "true");
  }
  return params.toString();
}

export function fetchHeatmap(
  days: number,
  filters?: HeatmapFilters,
): Promise<HeatmapResponse> {
  const qs = buildHeatmapQuery(days, filters);
  return apiGet<HeatmapResponse>(`${BASE}/anomaly-heatmap?${qs}`);
}

export function fetchSnapshots(
  cpuId: string,
  date: string | null,
  cursor: string | null,
  limit = 50,
): Promise<SnapshotPage> {
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  if (cursor) params.set("cursor", cursor);
  params.set("limit", String(limit));
  return apiGet<SnapshotPage>(
    `${BASE}/snapshots/${encodeURIComponent(cpuId)}?${params.toString()}`,
  );
}

export function fetchEvents(
  cpuId: string,
  filters: EventFilters,
  cursor: string | null = null,
  limit = 100,
): Promise<EventPage> {
  const params = new URLSearchParams();
  if (filters.date) params.set("date", filters.date);
  if (filters.type) params.set("type", filters.type);
  if (filters.severity) params.set("severity", filters.severity);
  if (cursor) params.set("cursor", cursor);
  params.set("limit", String(limit));
  return apiGet<EventPage>(
    `${BASE}/events/${encodeURIComponent(cpuId)}?${params.toString()}`,
  );
}

export function fetchTimeline(
  cpuId: string,
  hours = 24,
): Promise<TimelineResponse> {
  const params = new URLSearchParams();
  params.set("hours", String(hours));
  return apiGet<TimelineResponse>(
    `${BASE}/${encodeURIComponent(cpuId)}/timeline?${params.toString()}`,
  );
}

/**
 * Fetch sunrise/sunset for a (cpu_id, date) pair.
 *
 * Returns `null` on 404 — the machine has no lat/lon set, which is the
 * D-LL-04 empty-state, NOT an error. KioskTimelinePanel reads `null`
 * to render the `noSunOverlay` Alert instead of crashing.
 *
 * 503 (smoke_test fail, Pitfall 6 H3-data not bundled) is still thrown
 * because it's a real backend health issue worth surfacing to the
 * operator.
 */
export async function fetchSunTimes(
  cpuId: string,
  date: string,
): Promise<SunTimesResponse | null> {
  const params = new URLSearchParams();
  params.set("cpu_id", cpuId);
  params.set("date", date);
  try {
    return await apiGet<SunTimesResponse>(
      `${BASE}/sun-times?${params.toString()}`,
    );
  } catch (err) {
    const e = err as ApiError;
    if (e.status === 404) return null;
    throw err;
  }
}

export function fetchSummary(): Promise<SummaryResponse> {
  return apiGet<SummaryResponse>(`${BASE}/summary`);
}

export function fetchFlaggedCount(): Promise<FlaggedCountResponse> {
  return apiGet<FlaggedCountResponse>(`${BASE}/flagged-count`);
}
